import type { HelpDocument, HelpDocumentId, HelpGuide, HelpGuideId } from './types';

const DEFAULT_RESULT_LIMIT = 5;
const MAX_SNIPPET_LENGTH = 700;
const MIN_TOKEN_LENGTH = 2;
const STOPWORDS = new Set([
    'about',
    'can',
    'does',
    'do',
    'for',
    'from',
    'help',
    'how',
    'into',
    'is',
    'of',
    'the',
    'to',
    'use',
    'using',
    'what',
    'when',
    'where',
    'with',
]);
const SYNONYMS: Record<string, readonly string[]> = {
    add: ['create', 'open'],
    chart: ['charting'],
    connect: ['channel', 'link', 'linking'],
    delete: ['close', 'remove'],
    file: ['csv', 'upload'],
    import: ['csv', 'upload'],
    list: ['watchlist'],
    order: ['orders', 'trade'],
    panel: ['widget'],
    panels: ['widget', 'widgets'],
    screen: ['screener', 'searchalpha'],
    screener: ['screen', 'searchalpha'],
    stock: ['security', 'securities', 'symbol'],
    stocks: ['security', 'securities', 'symbol'],
    ticker: ['symbol'],
    tickers: ['symbol', 'symbols'],
    together: ['channel', 'link', 'linking'],
};

export type HelpCenterSearchResult =
    | {
          type: 'document';
          id: HelpDocumentId;
          title: string;
          path: string[];
          snippet: string;
      }
    | {
          type: 'guide';
          id: HelpGuideId;
          title: string;
          path: string[];
          stepTitle?: string;
          snippet: string;
      };

type SearchHelpCenterOptions = {
    documents: readonly HelpDocument[];
    guides: readonly HelpGuide[];
    limit?: number;
};

type ScoredResult = {
    result: HelpCenterSearchResult;
    score: number;
    sortTitle: string;
    sortId: string;
};

const normalize = (value: string) => value.toLowerCase().replace(/\s+/g, ' ').trim();

const stripMarkdown = (value: string) =>
    value
        .replace(/^#{1,6}\s+/gm, '')
        .replace(/```[\s\S]*?```/g, ' ')
        .replace(/`([^`]+)`/g, '$1')
        .replace(/\[([^\]]+)]\([^)]+\)/g, '$1')
        .replace(/[*_~>#-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

const tokenize = (query: string) =>
    normalize(query)
        .split(/[^a-z0-9]+/)
        .filter((token) => token.length >= MIN_TOKEN_LENGTH && !STOPWORDS.has(token));

const expandTokens = (tokens: readonly string[]) => [
    ...new Set(tokens.flatMap((token) => [token, ...(SYNONYMS[token] ?? [])])),
];

const scoreText = (value: string, normalizedQuery: string, tokens: readonly string[], weight: number) => {
    const normalizedValue = normalize(stripMarkdown(value));
    if (!normalizedValue) {
        return 0;
    }

    let score = normalizedValue.includes(normalizedQuery) ? weight * 2 : 0;
    for (const token of tokens) {
        if (normalizedValue.includes(token)) {
            score += weight;
        }
    }

    return score;
};

const createSnippet = (value: string, normalizedQuery: string, tokens: readonly string[]) => {
    const text = stripMarkdown(value);
    if (text.length <= MAX_SNIPPET_LENGTH) {
        return text;
    }

    const normalizedText = text.toLowerCase();
    const matchIndex = [normalizedQuery, ...tokens]
        .map((candidate) => normalizedText.indexOf(candidate))
        .find((index) => index >= 0);
    const start = Math.max(0, (matchIndex ?? 0) - 100);
    const end = Math.min(text.length, start + MAX_SNIPPET_LENGTH);

    return `${start > 0 ? '...' : ''}${text.slice(start, end).trim()}${end < text.length ? '...' : ''}`;
};

const pickDocumentSnippetSource = (document: HelpDocument, normalizedQuery: string, tokens: readonly string[]) => {
    if (scoreText(document.preview, normalizedQuery, tokens, 1) > 0) {
        return document.preview;
    }

    return document.content;
};

const scoreDocument = (
    document: HelpDocument,
    normalizedQuery: string,
    tokens: readonly string[],
): ScoredResult | null => {
    const score =
        scoreText(document.title, normalizedQuery, tokens, 40) +
        scoreText(document.path.join(' '), normalizedQuery, tokens, 30) +
        scoreText(document.keywords?.join(' ') ?? '', normalizedQuery, tokens, 30) +
        scoreText(document.preview, normalizedQuery, tokens, 10) +
        scoreText(document.content, normalizedQuery, tokens, 5);

    if (score === 0) {
        return null;
    }

    return {
        result: {
            type: 'document',
            id: document.id,
            title: document.title,
            path: document.path,
            snippet: createSnippet(
                pickDocumentSnippetSource(document, normalizedQuery, tokens),
                normalizedQuery,
                tokens,
            ),
        },
        score,
        sortTitle: document.title,
        sortId: document.id,
    };
};

const scoreGuide = (guide: HelpGuide, normalizedQuery: string, tokens: readonly string[]): ScoredResult | null => {
    const metadataScore =
        scoreText(guide.title, normalizedQuery, tokens, 40) +
        scoreText(guide.path.join(' '), normalizedQuery, tokens, 30) +
        scoreText(guide.keywords?.join(' ') ?? '', normalizedQuery, tokens, 30) +
        scoreText(guide.description, normalizedQuery, tokens, 10);

    const bestStep = guide.steps
        .map((step) => ({
            step,
            score:
                scoreText(step.title, normalizedQuery, tokens, 25) +
                scoreText(step.content, normalizedQuery, tokens, 8),
        }))
        .sort((a, b) => b.score - a.score || a.step.title.localeCompare(b.step.title))[0];
    const stepScore = bestStep?.score ?? 0;
    const score = metadataScore + stepScore;

    if (score === 0) {
        return null;
    }

    const useStepSnippet = bestStep !== undefined && stepScore >= metadataScore;

    return {
        result: {
            type: 'guide',
            id: guide.id,
            title: guide.title,
            path: guide.path,
            ...(useStepSnippet ? { stepTitle: bestStep.step.title } : {}),
            snippet: createSnippet(useStepSnippet ? bestStep.step.content : guide.description, normalizedQuery, tokens),
        },
        score,
        sortTitle: guide.title,
        sortId: guide.id,
    };
};

export function searchHelpCenter(query: string, options: SearchHelpCenterOptions): HelpCenterSearchResult[] {
    const normalizedQuery = normalize(query);
    const tokens = expandTokens(tokenize(query));
    if (normalizedQuery.length < MIN_TOKEN_LENGTH || tokens.length === 0) {
        return [];
    }

    const documents = options.documents;
    const guides = options.guides;
    const limit = options.limit ?? DEFAULT_RESULT_LIMIT;

    return [
        ...documents.map((document) => scoreDocument(document, normalizedQuery, tokens)),
        ...guides.map((guide) => scoreGuide(guide, normalizedQuery, tokens)),
    ]
        .filter((result): result is ScoredResult => result !== null)
        .sort(
            (a, b) =>
                b.score - a.score ||
                a.sortTitle.localeCompare(b.sortTitle) ||
                a.sortId.localeCompare(b.sortId) ||
                a.result.type.localeCompare(b.result.type),
        )
        .slice(0, limit)
        .map((result) => result.result);
}
